// modules/tenant-builder/TenantBuilder.tsx - Construtor Principal de Tenants

import React, { useState } from 'react';
import { 
  CheckCircle, 
  Building2, 
  Palette, 
  LayoutGrid,
  Settings,
  Eye,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  MapPin
} from 'lucide-react';
import { Tenant, TenantPage } from '../../core/types';
import { BusinessInfoStep } from './steps/BusinessInfoStep';
import { TemplateStep } from './steps/TemplateStep';
import { CustomizationStep } from './steps/CustomizationStep';
import { ModulesStep } from './steps/ModulesStep';
import { ReviewStep } from './steps/ReviewStep';
import { PortalDataStep } from './steps/PortalDataStep';
import { generateSubdomain } from '../../core/utils/domainHelper';
import { tenantService } from '../../core/services/firebase/firestore';
import { getTemplateById } from '../../templates';

const STEPS = [
  { id: 'business', title: 'Informações', icon: Building2, description: 'Dados do seu negócio' },
  { id: 'portal', title: 'Portal', icon: MapPin, description: 'Localização e Categoria' },
  { id: 'template', title: 'Template', icon: LayoutGrid, description: 'Escolha o visual' },
  { id: 'customize', title: 'Personalização', icon: Palette, description: 'Cores e logo' },
  { id: 'modules', title: 'Módulos', icon: Settings, description: 'Funcionalidades' },
  { id: 'review', title: 'Revisão', icon: Eye, description: 'Confirmar e criar' },
];

export const TenantBuilder: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [tenant, setTenant] = useState<Partial<Tenant>>({
    enabledModules: {
      bookings: true,
      gallery: true,
      blog: false,
      ecommerce: false,
      reviews: true,
      team: true,
      fishingLog: true,
    },
    settings: {
      language: 'pt',
      currency: 'BRL',
      timezone: 'America/Sao_Paulo',
      dateFormat: 'DD/MM/YYYY',
      privacy: 'public',
    },
    branding: {
      logoUrl: '',
      faviconUrl: '',
      primaryColor: '#1e40af',
      secondaryColor: '#dc2626',
      accentColor: '#059669',
      fontFamily: 'Inter',
      theme: 'light',
    }
  });

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      createTenant();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const updateTenant = (updates: Partial<Tenant>) => {
    setTenant(prev => ({ ...prev, ...updates }));
  };

  const createTenant = async () => {
    try {
      setIsCreating(true);

      const selectedTemplate = getTemplateById(tenant.templateId || 'pousada-classica');

      // Gerar subdomínio único
      const subdomain = generateSubdomain(tenant.businessName!);
      const tenantId = `tenant-${Date.now()}-${subdomain}`;

      const newTenant: Tenant = {
        id: tenantId,
        
        // Novos campos do Portal (Inicialização Padrão)
        tenantType: (tenant.businessType === 'association' ? 'associacao' : 
                     tenant.businessType === 'fishing_pond' ? 'pesqueiro' :
                     tenant.businessType === 'tour_operator' ? 'agencia' :
                     tenant.businessType === 'fishing_guide' ? 'guia' :
                     tenant.businessType === 'hotel' ? 'hotel' : 'pousada'), 
        tier: 'free-listing',
        location: {
           state: 'GO', // Placeholder
           city: '',
           touristicRegion: ''
        },
        portalProfile: {
           shortDescription: '',
           highlights: [],
           contact: {
              email: tenant.email!,
              phone: tenant.phone!
           }
        },
        categoryTags: [],

        businessName: tenant.businessName!,
        businessType: tenant.businessType!,
        description: tenant.description || '',
        slogan: tenant.slogan,
        email: tenant.email!,
        phone: tenant.phone!,
        whatsapp: tenant.whatsapp,
        address: tenant.address,
        domain: tenant.domain || `${subdomain}.com.br`,
        subdomainStaging: `${subdomain}.pescar.app`,
        customDomains: tenant.domain ? [tenant.domain] : [],
        // Merge branding with template defaults if needed
        branding: {
             ...tenant.branding!,
             primaryColor: tenant.branding?.primaryColor || selectedTemplate.defaultColors.primary,
             secondaryColor: tenant.branding?.secondaryColor || selectedTemplate.defaultColors.secondary,
             accentColor: tenant.branding?.accentColor || selectedTemplate.defaultColors.accent,
        },
        settings: tenant.settings!,
        enabledModules: tenant.enabledModules!,
        templateId: selectedTemplate.id,
        // Create full TenantPage objects from template defaults
        pages: selectedTemplate.defaultPages.map((p, index) => ({
          ...p,
          id: `page-${p.slug}`,
          type: p.slug === 'home' ? 'home' : 'custom',
          isPublished: true,
          isDraft: false,
          order: index + 1,
          showInNavigation: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        }) as TenantPage),
        navigation: [], // Could be derived from pages if they have 'title'
        status: 'pending_dns',
        plan: 'basic',
        trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 dias
        createdAt: new Date(),
        updatedAt: new Date(),
        lastActiveAt: new Date(),
        visits: 0,
      };

      // Salvar no Firebase
      await tenantService.createTenant(newTenant);

      // Redirecionar para o site staging
      // Redirecionar para o site staging
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        alert(`✅ Site criado com sucesso! (Modo Desenvolvimento)\n\nVocê será redirecionado para o novo tenant: ${tenantId}`);
        localStorage.setItem('dev_tenant_id', tenantId);
        window.location.href = '/';
      } else {
        alert(`✅ Site criado com sucesso!\n\nSeu site está disponível em:\n${newTenant.subdomainStaging}\n\nRedirecionando...`);
        window.location.href = `https://${newTenant.subdomainStaging}`;
      }
      
    } catch (error) {
      console.error('Erro ao criar tenant:', error);
      alert('❌ Erro ao criar o site. Tente novamente.');
    } finally {
      setIsCreating(false);
    }
  };

  const currentStepConfig = STEPS[currentStep];
  const Icon = currentStepConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white p-2 rounded-lg">
                <Sparkles size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Crie seu Site de Pescaria
                </h1>
                <p className="text-sm text-gray-600">
                  Em 5 minutos, tenha um site profissional para sua pousada
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-gray-500">Plano</div>
                <div className="font-semibold text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  ✨ Teste Grátis 14 dias
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center relative flex-1">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
                      index < currentStep 
                        ? 'bg-green-500 text-white shadow-lg' 
                        : index === currentStep
                        ? 'bg-blue-600 text-white border-4 border-blue-100 shadow-xl scale-110' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index < currentStep ? (
                        <CheckCircle size={28} />
                      ) : (
                        <StepIcon size={28} />
                      )}
                    </div>
                    <div className={`mt-3 text-center ${
                      index <= currentStep ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      <div className="font-semibold text-sm">{step.title}</div>
                      <div className="text-xs text-gray-500">{step.description}</div>
                    </div>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-500 ${
                      index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8 min-h-[500px]">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b">
            <Icon className="text-blue-600" size={32} />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{currentStepConfig.title}</h2>
              <p className="text-gray-600">{currentStepConfig.description}</p>
            </div>
          </div>

          {currentStep === 0 && (
            <BusinessInfoStep 
              data={tenant} 
              onChange={updateTenant} 
            />
          )}
          {currentStep === 1 && (
            <PortalDataStep 
              data={tenant} 
              onChange={updateTenant} 
            />
          )}
          {currentStep === 2 && (
            <TemplateStep 
              data={tenant} 
              onChange={updateTenant} 
            />
          )}
          {currentStep === 3 && (
            <CustomizationStep 
              data={tenant} 
              onChange={updateTenant} 
            />
          )}
          {currentStep === 4 && (
            <ModulesStep 
              data={tenant} 
              onChange={updateTenant} 
            />
          )}
          {currentStep === 5 && (
            <ReviewStep 
              data={tenant as Tenant} 
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
            }`}
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
          
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="font-semibold text-blue-600">{currentStep + 1}</span>
            <span>de</span>
            <span>{STEPS.length}</span>
          </div>
          
          <button
            onClick={handleNext}
            disabled={isCreating}
            className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-white transition-all ${
              isCreating
                ? 'bg-gray-400 cursor-wait'
                : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 hover:shadow-xl'
            }`}
          >
            {isCreating ? (
              <>
                <span className="animate-spin">⚙️</span>
                Criando...
              </>
            ) : currentStep === STEPS.length - 1 ? (
              <>
                <Sparkles size={20} />
                Criar Meu Site!
              </>
            ) : (
              <>
                Continuar
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
